/* AI gateway spend: every enabled budget as a row (blocked = act, past its alert threshold = watch), with 7-day prod
   spend, top apps and failures in the lead. Only sees traffic routed through the gateway; direct provider calls
   (e.g. VidMax's Kie/fal/Replicate jobs) aren't in it. */
import { parseGatewaySubjectApplicationId } from "@aix/jsutils/src/aiGatewayPolicy"
import type { inferRouterOutputs } from "@trpc/server"
import { useCallback } from "react"

import type { AppRouter } from "../../../../../aixinternal-api/src/router/_app"
import { trpc } from "../../../lib/trpc"
import { worstVerdict } from "../homeDashboardModel"
import { EVerdict } from "../homeDashboard.types"
import type { IWidgetDefinition, IWidgetResult, IWidgetRow } from "../homeDashboard.types"
import { KeyValues } from "./WidgetParts"
import { WIDGET_QUERY_OPTIONS, useQueryWidget } from "./widgetQuery"

type TOutputs = inferRouterOutputs<AppRouter>["aiGateway"]
type TPolicy = TOutputs["policies"]["rows"][number]
type TUsage = TOutputs["usage"]

const MICRO_PER_USD = 1_000_000
const HOUR_MS = 3_600_000
const USAGE_DAYS = 7
const TOP_APPS = 3
const FULL_PCT = 100

const usd = (micro: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(
    micro / MICRO_PER_USD,
  )

const usedPct = (used: number, limit: number) => (limit > 0 ? Math.round((used / limit) * FULL_PCT) : 0)

const policyName = (policy: TPolicy) => {
  if (policy.scope === "environment") return `All of ${policy.environment}`
  if (policy.scope === "subject_application") {
    const pair = parseGatewaySubjectApplicationId(policy.scopeId)
    return pair ? `${pair[0]} · ${pair[1]}` : policy.scopeId
  }
  return policy.scopeId
}

/** Application budgets belong to that app's company; environment and per-developer budgets are cross-company */
const policyCompany = (policy: TPolicy): string | undefined => {
  if (policy.scope === "application") return policy.scopeId
  if (policy.scope === "subject_application") return parseGatewaySubjectApplicationId(policy.scopeId)?.[1]
  return undefined
}

const policyRow = (policy: TPolicy): IWidgetRow => {
  const daily = usedPct(policy.dailyUsedMicroUsd, policy.dailyLimitMicroUsd)
  const monthly = usedPct(policy.monthlyUsedMicroUsd, policy.monthlyLimitMicroUsd)
  const worst = Math.max(daily, monthly)
  let verdict = EVerdict.CLEAR
  if (worst >= FULL_PCT) verdict = EVerdict.ACT
  else if (worst >= policy.alertThresholdPercent) verdict = EVerdict.WATCH
  return {
    key: `${policy.scope}:${policy.scopeId}:${policy.environment}`,
    company: policyCompany(policy),
    verdict,
    pill: { text: worst >= FULL_PCT ? "blocked" : `${worst}%`, verdict },
    title: policyName(policy),
    sub: `${policy.scope.replace("_", " ")} · ${policy.environment} · today ${daily}% · month ${monthly}%`,
    meta: `${usd(policy.monthlyUsedMicroUsd)} / ${usd(policy.monthlyLimitMicroUsd)}`,
    quiet: verdict === EVerdict.CLEAR,
    details: (
      <KeyValues
        pairs={[
          [
            "Today",
            `${usd(policy.dailyUsedMicroUsd)} of ${usd(policy.dailyLimitMicroUsd)} (${usd(policy.dailyRemainingMicroUsd)} left)`,
          ],
          [
            "This month",
            `${usd(policy.monthlyUsedMicroUsd)} of ${usd(policy.monthlyLimitMicroUsd)} (${usd(policy.monthlyRemainingMicroUsd)} left)`,
          ],
          ["Alerts at", `${policy.alertThresholdPercent}%`],
        ]}
      />
    ),
    href: "/ai-gateway",
    hrefLabel: "Open gateway",
  }
}

const topApps = (usage: TUsage) => {
  const byApp = new Map<string, number>()
  for (const row of usage.rows) byApp.set(row.app, (byApp.get(row.app) ?? 0) + row.costMicroUsd)
  return [...byApp.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, TOP_APPS)
    .map(([app, micro]) => `${app} ${usd(micro)}`)
    .join(" · ")
}

const renderSpend = (policies: TPolicy[], usage: TUsage | undefined): IWidgetResult => {
  const rows = policies.filter((p) => p.enabled).map(policyRow)
  const blocked = rows.filter((r) => r.verdict === EVerdict.ACT).length
  const failures = usage?.metrics?.failures ?? 0
  return {
    verdict: worstVerdict(rows.map((r) => r.verdict)),
    count: blocked ? `${blocked} blocked` : `${rows.length} budgets`,
    lead: usage ? (
      <KeyValues
        pairs={[
          [
            `Prod ${USAGE_DAYS}d`,
            `${usd(usage.totals.costMicroUsd)} · ${usage.totals.requests.toLocaleString()} requests`,
          ],
          ["Top apps", topApps(usage) || null],
          ["Failures", failures ? `${failures} failed · ${usage.metrics?.unsettled ?? 0} unsettled` : null],
        ]}
      />
    ) : undefined,
    rows,
    quietLabel: `${rows.filter((r) => r.quiet).length} under threshold`,
    empty: "No gateway budgets set.",
    foot: "Gateway traffic only; direct provider calls aren't counted.",
  }
}

export const gatewaySpendWidget: IWidgetDefinition = {
  id: "gateway-spend",
  title: "AI gateway spend",
  description: "Gateway budgets near or over their limit, plus 7-day prod spend",
  columns: { 2: 2, 3: 3, 4: 3, 5: 4, 6: 4 },
  link: { href: "/ai-gateway", label: "Open AI gateway" },
  useWidget: () => {
    // Hour-aligned window so the query key only changes once an hour
    const hour = Math.floor(Date.now() / HOUR_MS) * HOUR_MS
    const usage = trpc.aiGateway.usage.useQuery(
      { environment: "prod", from: new Date(hour - USAGE_DAYS * 24 * HOUR_MS).toISOString() },
      WIDGET_QUERY_OPTIONS,
    )
    const render = useCallback((data: { rows: TPolicy[] }) => renderSpend(data.rows, usage.data), [usage.data])
    return useQueryWidget(trpc.aiGateway.policies.useQuery(undefined, WIDGET_QUERY_OPTIONS), render)
  },
}
